import React, { Suspense } from "react";
import ProtectedRoute from "ProtectedRoute";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

// Site Builder routes. Everything lives under /site/* because nginx routes that
// prefix on the org subdomain to this app's dev server — a path prefix rather
// than a separate hostname, so the localStorage auth token (and therefore the
// session handed over from the backoffice) still applies.
const SiteLayoutList = React.lazy(() => import("site/pages/Layouts"));
const SiteDesigner = React.lazy(() => import("site/pages/Designer"));
const SiteViewer = React.lazy(() => import("site/pages/Viewer"));

// Facility Map (F2-SPEC-MAP-01): every site's floors, layers, pin status,
// walkthroughs and KPIs for one organization, asked for on entry.
const FacilityMap = React.lazy(() => import("site/pages/FacilityMap"));

// Settings sub-pages — thin wrappers around the AFMS equivalents, kept so an
// operator who lands here can fix an asset's location or data link without
// bouncing back to the backoffice.
const SiteSettingsOrganization = React.lazy(() => import("site/pages/Settings/Organization"));
const SiteSettingsUsers = React.lazy(() => import("site/pages/Settings/Users"));
const SiteSettingsAssets = React.lazy(() => import("site/pages/Settings/Assets"));
const SiteSettingsDevices = React.lazy(() => import("site/pages/Settings/Devices"));
const SiteAssetEdit = React.lazy(() => import("site/pages/AssetEdit"));

// My Inbox — the shared notifications-api inbox. Same screen the Environmental
// and Energy portals mount; rows are scoped to the caller by the server, so this
// surfaces one inbox in another portal rather than a Site-specific feed.
const SiteInbox = React.lazy(() => import("site/pages/Inbox"));

const wrap = (Component) => (
  <ProtectedRoute
    component={
      <Suspense fallback={null}>
        <Component />
      </Suspense>
    }
  />
);

const siteRoutes = [
  {
    // Landing page: pick an org + location, then a layout to edit or view.
    type: "collapse",
    name: "Site Layouts",
    key: "site-layouts",
    icon: <MapOutlinedIcon />,
    route: "/site/builder",
    component: wrap(SiteLayoutList),
    noCollapse: true,
  },
  {
    // The WYSIWYG editor. Hidden from the sidebar — it is always reached from a
    // layout row, and it needs a ?layout= to mean anything.
    type: "hidden",
    key: "site-designer",
    route: "/site/builder/design",
    component: wrap(SiteDesigner),
  },
  {
    // Read-only viewer. This is the screen that later becomes client-facing, so
    // it deliberately carries no builder-only chrome.
    type: "hidden",
    key: "site-viewer",
    route: "/site/view",
    component: wrap(SiteViewer),
  },
  {
    type: "collapse",
    name: "Facility Map",
    key: "site-facility-map",
    icon: <LayersOutlinedIcon />,
    route: "/site/facility-map",
    component: wrap(FacilityMap),
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "My Inbox",
    key: "site-inbox",
    route: "/site/inbox",
    icon: <MailOutlineIcon fontSize="small" />,
    component: wrap(SiteInbox),
    noCollapse: true,
  },

  // ── Settings sub-pages (sidebar entries live in SitePortalNavigation.js) ──
  {
    type: "hidden",
    key: "site-settings-organization",
    route: "/site/settings/organization",
    component: wrap(SiteSettingsOrganization),
  },
  {
    type: "hidden",
    key: "site-settings-users",
    route: "/site/settings/users",
    component: wrap(SiteSettingsUsers),
  },
  {
    type: "hidden",
    key: "site-settings-assets",
    route: "/site/settings/assets",
    component: wrap(SiteSettingsAssets),
  },
  {
    type: "hidden",
    key: "site-settings-devices",
    route: "/site/settings/devices",
    component: wrap(SiteSettingsDevices),
  },
  {
    // Asset editor — the "configure data links" target from an icon's config
    // panel, so a missing metric can be bound without leaving the app.
    type: "hidden",
    key: "site-asset-edit",
    route: "/site/assets/edit",
    component: wrap(SiteAssetEdit),
  },
];

export default siteRoutes;
